/** Request body UI modes (Body tab). */
export const BODY_UI_MODES = ['none', 'raw', 'form-data', 'urlencoded', 'graphql'];

/** Raw body editor / Content-Type languages (Postman-style subset). */
export const RAW_BODY_LANGUAGES = ['text', 'json', 'xml'];

export function normalizeRawBodyLanguage(value) {
  return RAW_BODY_LANGUAGES.includes(value) ? value : 'json';
}

/**
 * Normalize tab/API/sidebar body fields to UI mode + raw language.
 * Migrates legacy bodyType values `json` and `text` into raw + language.
 */
export function normalizeRequestBodyMode(source) {
  const settingsLang = source?.settings?.raw_body_language;
  const explicitLang = source?.rawBodyLanguage ?? settingsLang;

  if (source?.settings?.ui_body_mode === 'none') {
    return { bodyType: 'none', rawBodyLanguage: normalizeRawBodyLanguage(explicitLang || 'json') };
  }

  let bodyType = source?.bodyType ?? source?.body_type ?? 'none';
  if (bodyType === 'formdata') bodyType = 'form-data';
  if (bodyType === 'x-www-form-urlencoded') bodyType = 'urlencoded';
  // Importers (Postman/Insomnia/HAR/Bruno/OpenAPI) persist urlencoded as `form`.
  if (bodyType === 'form') bodyType = 'urlencoded';

  if (bodyType === 'none') {
    return { bodyType: 'none', rawBodyLanguage: normalizeRawBodyLanguage(explicitLang || 'json') };
  }

  if (bodyType === 'form-data') {
    return { bodyType: 'form-data', rawBodyLanguage: normalizeRawBodyLanguage(explicitLang) };
  }

  if (bodyType === 'urlencoded') {
    return { bodyType: 'urlencoded', rawBodyLanguage: normalizeRawBodyLanguage(explicitLang) };
  }

  if (bodyType === 'json') {
    return { bodyType: 'raw', rawBodyLanguage: 'json' };
  }
  if (bodyType === 'text') {
    return { bodyType: 'raw', rawBodyLanguage: 'text' };
  }
  if (bodyType === 'raw') {
    return {
      bodyType: 'raw',
      rawBodyLanguage: normalizeRawBodyLanguage(explicitLang || 'text'),
    };
  }

  if (bodyType === 'graphql') {
    return { bodyType: 'graphql', rawBodyLanguage: 'json' };
  }

  return { bodyType: 'none', rawBodyLanguage: normalizeRawBodyLanguage(explicitLang || 'json') };
}

export function isNoBodyMode(bodyType) {
  return bodyType === 'none';
}

export function isRawBodyMode(bodyType) {
  const t = bodyType === 'formdata' ? 'form-data' : bodyType;
  return t !== 'form-data' && t !== 'urlencoded' && t !== 'graphql' && t !== 'none';
}

export function isGraphQLBodyMode(bodyType) {
  return bodyType === 'graphql';
}

export function isUrlencodedBodyMode(bodyType) {
  return bodyType === 'urlencoded';
}

/** API body_type from UI mode + raw language. */
export function bodyTypeForApi(bodyType, rawBodyLanguage) {
  const { bodyType: mode, rawBodyLanguage: lang } = normalizeRequestBodyMode({
    bodyType,
    rawBodyLanguage,
  });
  if (mode === 'form-data') return 'formdata';
  if (mode === 'urlencoded') return 'urlencoded';
  if (mode === 'graphql') return 'graphql';
  if (mode === 'none') return 'raw';
  if (lang === 'json') return 'json';
  return 'raw';
}

export function contentTypeForRawLanguage(language) {
  switch (normalizeRawBodyLanguage(language)) {
    case 'json':
      return 'application/json';
    case 'xml':
      return 'application/xml';
    default:
      return 'text/plain';
  }
}

export function codeEditorLanguageForBody(bodyType, rawBodyLanguage) {
  if (!isRawBodyMode(bodyType)) return 'json';
  return normalizeRawBodyLanguage(rawBodyLanguage);
}

export function bodyPlaceholderKey(rawBodyLanguage) {
  switch (normalizeRawBodyLanguage(rawBodyLanguage)) {
    case 'json':
      return 'request.jsonBodyPlaceholder';
    case 'xml':
      return 'request.xmlBodyPlaceholder';
    default:
      return 'request.enterRequestBody';
  }
}
